import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import api from "@services/axios/apiManager";
import { IdentitySwitchMenu, MemberCircleList } from "@components/IdentitySwitchMenu";
import { clearHomeCache, readHomeCache, writeHomeCache } from "./homeCache";
import { readLastContextId } from "@utils/store/user/lastContext";
import {
  RiLineChartLine,
  RiSparkling2Line,
  RiCalendarEventLine,
  RiArrowRightLine,
  RiArrowRightSLine,
  RiPhoneLine,
  RiQuestionLine,
  RiShieldCheckLine,
  RiErrorWarningLine,
  RiWindyLine,
  RiShutDownLine,
  RiFlaskLine,
} from "react-icons/ri";
import { getFirstText } from "@utils/func/textTool";
import toast from "react-hot-toast";
import { toToastMessage } from "@utils/toastMessage";
import useHomePageSheetStore from "@utils/store/useHomePageSheetStore";
import { buildXContextHeader } from "@utils/api/headers";
import {
  buildIntervalGasViewModel,
  toMonthDay,
  getTodayIso,
  addDaysToIsoDate,
  type HomeGasStatusInterval,
} from "./intervalGasView.model";
import { GasPredictionHeroContent, OrderCtaButton, ReminderStageBlock, STATUS_LABEL_COLOR } from "./intervalGasView.ui";
import { DAY_NUMBER_FONT } from "./homeSkin";
import { FeedbackBlock } from "./feedbackBlock.ui";
import { CycleOutcomeCard } from "./cycleOutcomeCard.ui";
import {
  DEV_OUTCOME_OPTIONS,
  clearCycleNotifyPause,
  clearCycleOutcomeAck,
  isUrgentCycleOutcome,
  resolveCycleOutcomeKind,
  type CycleOutcomeKind,
} from "./cycleOutcome.model";
import { postCycleFeedback, postCyclePause } from "./cycleFeedbackApi";
import { type IntervalRangeFontKey, INTERVAL_RANGE_FONT_OPTIONS } from "./intervalRangeFont";
import { getGasSurvey, saveGasSurvey, estimateCycleFromSurvey, needsGasSurvey, resolveSurveyAnchorDate, buildDevDefaultSurveyRecord, type GasSurveyRecord } from "@screen/Survey/surveyStorage";
import { postGasSurvey } from "@screen/Survey/gasSurveyApi";
import { InlineSurveyCard } from "./inlineSurvey.ui";
import { AccountCouponCards, FaqCard } from "./homeInfoCards.ui";
import { ColdStartNewHero, COLD_HEADER_SHAPE_OPTIONS, type ColdHeaderShapeKey } from "./coldStartNewHero.ui";
import { SurveyCycleEstimateHero, SurveyPredictionPendingHero } from "./surveyCycleEstimate.ui";
import { SurveyUpdateModal } from "./surveyUpdateModal.ui";
import { track } from "@lib/tracker";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import FirstJoinSetup from "@screen/Kiosk/components/FirstJoinSetup";
import { usePracticeStore } from "@utils/practice/practiceMode";
import { HomeSkeleton } from "./homeSkeleton.ui";

// 記住已被 /customer/home 回 401（未綁定客戶）的 token → 同一 token 不再重打，
// 避免未綁定客戶每次進首頁都產生 401，減少 log 噪音。bound 客戶回 200 永不入集合；
// 換 token（重新登入/綁定後刷新）自然不在集合中，會正常重試。
const homeUnauthTokens = new Set<string>();

/**
 * 首頁一律不「開啟」全域遮罩，只負責關掉。
 * 開機那層遮罩是 LineLoginProvider 開的（它刻意不關，留給首頁接手，讓
 * splash → LIFF 登入 → 首頁不露白）；首頁自己再 setLoading(true) 的話，
 * 從瓦斯訂購／訂單切回首頁也會被整片蓋住，那不是開機。
 */

/* ── 色票（對齊原型）── */
const C = {
  blue: "#2196f3",
  blueDark: "#1e88e5",
  blueLight: "#4fb0f7",
  blueTint: "#e3f2fd",
  blueText: "#1976d2",
  page: "#f8fafc",
  ink: "#1f2937",
  muted: "#8b909c",
  green: "#1d9e75",
  greenTint: "#e1f5ee",
};

/* 沒有智慧預測時的說明 sheet（純說明數據不足無法預測） */
const NO_PREDICTION_SHEET = {
  header: "為什麼沒有智慧預測？",
  contentArray: [
    {
      title: "目前數據不足，無法預測",
      content: "您的叫桶紀錄還太少，系統暫時無法計算用量並提供智慧預測。",
    },
    {
      title: "平均叫桶天數",
      content: "根據您過去的叫桶紀錄，計算每桶平均使用天數。",
    },
    {
      title: "持續累積紀錄",
      content: "每次叫桶後資料都會持續累積，達到足夠筆數後即可提供更準確的預測。",
    },
  ],
  button: { buttonText: "我知道了" },
  fitContent: true,
};

const Grain = ({ o = 0.03 }: { o?: number }) => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
      backgroundRepeat: "repeat",
      backgroundSize: 200,
      opacity: o,
      pointerEvents: "none",
      borderRadius: "inherit",
      mixBlendMode: "soft-light",
    }}
  />
);

/* ── 玻璃桶 ── */
const GlassTank = ({
  percent = 65,
  low,
  noData = false,
  tone,
}: {
  percent?: number;
  low?: boolean;
  noData?: boolean;
  /** 與 Hero 狀態同步的液體色；未傳則依 percent 推估 */
  tone?: "充足" | "留意" | "偏低" | "建議補充";
}) => {
  const [ap, setAp] = useState(0);
  useEffect(() => {
    if (noData) {
      setAp(0);
      return;
    }
    let s = Date.now();
    const r = () => {
      const p = Math.min(1, (Date.now() - s) / 1000);
      setAp(percent * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(r);
    };
    r();
  }, [percent, noData]);

  const liqColors = (() => {
    const byTone = tone ?? (percent >= 60 ? "充足" : percent >= 40 ? "留意" : percent >= 25 ? "偏低" : "建議補充");
    switch (byTone) {
      case "充足":
        return { top: "#3B82F6", bottom: "#22D3EE", accent: "#3B82F6", wave: "#06B6D4" };
      case "留意":
        return { top: "#FDE68A", bottom: "#F59E0B", accent: "#FBBF24", wave: "#FCD34D" };
      case "偏低":
        return { top: "#FDBA74", bottom: "#FB923C", accent: "#F97316", wave: "#FDBA74" };
      case "建議補充":
      default:
        return { top: "#FCA5A5", bottom: "#F87171", accent: "#EF4444", wave: "#FCA5A5" };
    }
  })();
  const fy = noData ? 495 : 470 - (ap / 100) * 490;
  const glassFill = noData ? "rgba(180,190,205,0.45)" : "rgba(225,230,238,0.55)";
  const glassStroke = noData ? "rgba(200,210,225,0.55)" : "rgba(235,240,248,0.65)";

  return (
    <svg viewBox="0 -160 280 660" preserveAspectRatio="xMidYMax meet" fill="none" style={{ width: "100%", height: "100%", filter: "drop-shadow(0 18px 28px rgba(0,0,0,0.12))" }}>
      <defs>
        <linearGradient id="gtBody" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={glassFill} />
          <stop offset="50%" stopColor={noData ? "rgba(180,190,205,0.3)" : "rgba(225,230,238,0.4)"} />
          <stop offset="100%" stopColor={noData ? "rgba(180,190,205,0.4)" : "rgba(225,230,238,0.5)"} />
        </linearGradient>
        <linearGradient id="gtLiq" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={liqColors.top} stopOpacity="0.7" />
          <stop offset="100%" stopColor={liqColors.bottom} />
        </linearGradient>
        <clipPath id="gtClip">
          <path d="M42 -75 Q42 -110 77 -110 L203 -110 Q238 -110 238 -75 L238 460 Q238 495 203 495 L77 495 Q42 495 42 460 Z" />
        </clipPath>
      </defs>

      {/* 桶蓋 */}
      <path
        d="M92 -155 L188 -155 Q193 -155 193 -150 L193 -119 Q193 -114 188 -114 L92 -114 Q87 -114 87 -119 L87 -150 Q87 -155 92 -155 Z"
        fill={noData ? "rgba(180,190,205,0.3)" : "rgba(225,230,238,0.4)"}
        stroke={glassStroke}
        strokeWidth="2"
      />
      <rect x="96" y="-145" width="30" height="22" rx="2" fill="rgba(0,0,0,0.25)" stroke={glassStroke} strokeWidth="1.2" />
      <rect x="154" y="-145" width="30" height="22" rx="2" fill="rgba(0,0,0,0.25)" stroke={glassStroke} strokeWidth="1.2" />
      <rect x="124" y="-152" width="32" height="9" rx="2" fill={noData ? "rgba(180,190,205,0.4)" : "rgba(225,230,238,0.5)"} stroke={glassStroke} strokeWidth="1.2" />
      <rect x="135" y="-147" width="10" height="35" fill={noData ? "rgba(180,190,205,0.5)" : "rgba(225,230,238,0.6)"} stroke={glassStroke} strokeWidth="1.2" />

      {/* 桶身 */}
      <path
        d="M42 -75 Q42 -110 77 -110 L203 -110 Q238 -110 238 -75 L238 460 Q238 495 203 495 L77 495 Q42 495 42 460 Z"
        fill="url(#gtBody)"
        stroke={glassStroke}
        strokeWidth="2"
      />

      {/* 液體 */}
      <g clipPath="url(#gtClip)">
        {!noData && (
          <>
            <rect x="42" y={fy} width="196" height={495 - fy} fill="url(#gtLiq)" />
            <ellipse cx="140" cy={fy} rx="98" ry="5" fill={liqColors.accent} opacity="0.85" />
            <path d={`M36 ${fy + 2} Q85 ${fy - 3} 140 ${fy + 2} Q195 ${fy + 7} 244 ${fy + 2} L244 495 L36 495 Z`} fill={liqColors.wave} opacity="0.4">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;-12,0;0,0"
                dur="3s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.4 0 0.6 1;0.4 0 0.6 1"
              />
            </path>
            {!low && (
              <>
                <circle cx="90" cy={fy + 50} r="3" fill="rgba(255,255,255,0.7)">
                  <animate attributeName="cy" values={`${fy + 90};${fy + 15};${fy + 90}`} dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;0.7;0" dur="3s" repeatCount="indefinite" />
                </circle>
                <circle cx="185" cy={fy + 110} r="2.5" fill="rgba(255,255,255,0.6)">
                  <animate attributeName="cy" values={`${fy + 150};${fy + 25};${fy + 150}`} dur="2.5s" begin="0.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;0.6;0" dur="2.5s" begin="0.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="135" cy={fy + 170} r="2" fill="rgba(255,255,255,0.6)">
                  <animate attributeName="cy" values={`${fy + 210};${fy + 35};${fy + 210}`} dur="3.5s" begin="1s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;0.6;0" dur="3.5s" begin="1s" repeatCount="indefinite" />
                </circle>
              </>
            )}
          </>
        )}
      </g>

      {/* 高光 */}
      <rect x="52" y="-20" width="3" height="490" rx="1.5" fill={noData ? "rgba(180,190,205,0.4)" : "rgba(240,243,248,0.65)"} />
      <rect x="59" y="-15" width="2" height="480" rx="1" fill={noData ? "rgba(180,190,205,0.3)" : "rgba(240,243,248,0.45)"} />
    </svg>
  );
};

/* ── 開發測試：客戶狀態切換器（production build 不顯示）───────────────────
   列出所有客戶 UI 狀態，右上角浮動鈕切換預覽；資料為相對今天產生的 mock。 */

// 本地預覽：非 production 才啟用 DEV 狀態切換的邏輯
const IS_DEV = process.env.NODE_ENV !== "production";
// 右上角 DEV 狀態切換浮鈕：預設隱藏（連 staging 也不露出）；本地要用時改成 true。
/** 右上角 DEV 客戶狀態面板；正式關閉，本機要預覽再改 true */
const SHOW_DEV_SWITCHER = false;

/**
 * 狀態切換面板要不要出現。
 *
 * 練習模式也放行：練習是拿假資料在跑的示範環境（見 utils/practice/practiceMode.ts），
 * 本來就沒有真客戶資料，切換情境不會影響任何人；而要示範「充足／留意／偏低／建議補充」
 * 四種首頁長相，除了切狀態沒有別的辦法——真等瓦斯用到剩一成不可行。
 *
 * 兩條路徑互相獨立：本機開發改 SHOW_DEV_SWITCHER=true；練習部署則自動有。
 * 正式客戶（非練習、production build）兩條都不成立，面板一樣不存在。
 */
const useDevSwitcherVisible = (): boolean => {
  // 讀 store 而非 isPracticeMode()：練習模式可能在掛載後才啟用（從練習入口進來），
  // 直接讀 sessionStorage 的話面板不會跟著出現。
  const practice = usePracticeStore((st) => st.active);
  return (IS_DEV && SHOW_DEV_SWITCHER) || practice;
};

type DevScenarioKey =
  | "real"
  | "ai_ample"
  | "ai_notice"
  | "ai_low"
  | "ai_urgent"
  | "cold_avg"
  | "cold_new"
  | "survey_zero"
  | "survey_pending"
  | "join_setup"
  | "preparing"
  | "alert_early"
  | "alert_late"
  | "alert_irregular"
  | "outcome_soft"
  | "outcome_window"
  | "outcome_past_h"
  | "outcome_early"
  | "outcome_late";

type DevOutcomeForce = CycleOutcomeKind | "auto" | "none";

type DevSurveyMode = "real" | "todo" | "done";
type DevColdHeaderShape = ColdHeaderShapeKey | "auto";

interface DevHomeStats {
  avg_order_interval_days: number | null;
  last_order_date: string | null;
}

interface DevMockData {
  gasStatus: HomeGasStatusInterval | null;
  /** undefined = 沿用實際 home_stats */
  homeStats?: DevHomeStats;
}

const DEV_SCENARIO_LABELS: { key: DevScenarioKey; label: string; desc: string }[] = [
  { key: "real", label: "實際資料", desc: "走 API 回傳（預設）" },
  { key: "ai_ample", label: "AI・充足", desc: "提醒日前，CTA 顯示提醒日期" },
  { key: "ai_notice", label: "AI・留意", desc: "提醒日已到，琥珀 CTA 建議先預約" },
  { key: "ai_low", label: "AI・偏低", desc: "距建議出車 2 天" },
  { key: "ai_urgent", label: "AI・建議補充", desc: "進入用完窗，紅色 CTA 建議立即預約" },
  { key: "cold_avg", label: "冷啟動・有紀錄", desc: "問卷未填→平均 Hero；已填→日曆區間初估" },
  { key: "cold_new", label: "冷啟動・全新", desc: "問卷未填→尚無紀錄 Hero" },
  { key: "survey_zero", label: "0筆・問卷週期初估", desc: "無訂單＋已填→只顯示約每N天（本版）" },
  { key: "survey_pending", label: "0筆・預測準備中", desc: "問卷已收、predict_one 未就緒→防呆 pending" },
  { key: "join_setup", label: "註冊・尚未完成設定", desc: "0筆＋問卷初估＋完成加入設定橘卡" },
  { key: "preparing",        label: "預測整理中",       desc: "j_eligible 但缺 last_order" },
  { key: "alert_early",     label: "異常・用量增加",    desc: "behavior_alert type=early" },
  { key: "alert_late",      label: "異常・用量減少",    desc: "behavior_alert type=late" },
  { key: "alert_irregular", label: "異常・模式不規律",  desc: "behavior_alert type=irregular" },
  { key: "outcome_soft",    label: "一般・提醒日",      desc: "內容區 · notify～L" },
  { key: "outcome_window",  label: "一般・區間內",      desc: "內容區 · L～H" },
  { key: "outcome_early",   label: "一般・提早預約",    desc: "內容區 · 下單 < notify" },
  { key: "outcome_past_h",  label: "浮層・過區間未預約", desc: "底部浮層 · today > H" },
  { key: "outcome_late",    label: "浮層・較晚預約",    desc: "底部浮層 · 下單 > H" },
];

/** 以今天為基準產生完整 v2 欄位的 mock gasStatus */
const mkDevGasStatus = (o: {
  lastOrder: number;
  low: number;
  high: number;
  notify: number;
}): HomeGasStatusInterval => {
  const d = (n: number) => addDaysToIsoDate(getTodayIso(), n);
  const lDate = d(o.low);
  return {
    j_eligible: true,
    prediction_status: "可預測",
    predicted_days: o.high - o.lastOrder,
    last_order_date: d(o.lastOrder),
    depletion_low: lDate,
    depletion_high: d(o.high),
    dispatch_lower_date: lDate,
    observe_upper_date: d(o.high),
    notify_standard_date: d(o.notify),
    notify_light_date: d(o.notify - 5),
    notify_escalate_l3_date: d(o.low - 3),
    notify_escalate_l1_date: d(o.low - 1),
    notify_tier: "suggest",
    // DEV 預設過鬆 G；缺欄會走舊相容（仍顯示）
    interval_cv: 0.25,
    rolling_median_interval_days: Math.max(14, o.high - o.lastOrder),
    prediction_source: "model",
  };
};

const buildDevMock = (key: DevScenarioKey): DevMockData | null => {
  const d = (n: number) => addDaysToIsoDate(getTodayIso(), n);
  switch (key) {
    case "ai_ample":
      return { gasStatus: mkDevGasStatus({ lastOrder: -15, low: 32, high: 46, notify: 20 }) };
    case "ai_notice":
      return { gasStatus: mkDevGasStatus({ lastOrder: -40, low: 9, high: 20, notify: -1 }) };
    case "ai_low":
      return { gasStatus: mkDevGasStatus({ lastOrder: -45, low: 5, high: 14, notify: -3 }) };
    case "ai_urgent":
      return { gasStatus: mkDevGasStatus({ lastOrder: -55, low: -2, high: 8, notify: -9 }) };
    case "cold_avg":
      return {
        gasStatus: { j_eligible: false, prediction_status: "低信心", last_order_date: d(-20) },
        homeStats: { avg_order_interval_days: 45, last_order_date: d(-20) },
      };
    case "cold_new":
      return {
        gasStatus: null,
        homeStats: { avg_order_interval_days: null, last_order_date: null },
      };
    case "survey_zero":
      // 模擬後端已寫入 survey 預測但無真實訂單：不應走日曆區間
      return {
        gasStatus: {
          j_eligible: false,
          auto_eligible: false,
          prediction_status: "問卷初估",
          prediction_source: "survey",
          predicted_days: 30,
          last_order_date: null,
        },
        homeStats: { avg_order_interval_days: null, last_order_date: null },
      };
    case "survey_pending":
      // 問卷已入庫、predict_one 失敗／尚未寫入：防呆 pending Hero
      return {
        gasStatus: null,
        homeStats: { avg_order_interval_days: null, last_order_date: null },
      };
    case "join_setup":
      // 新註冊尚未完成 FirstJoinSetup：週期初估 + 橘卡同時出現
      return {
        gasStatus: {
          j_eligible: false,
          auto_eligible: false,
          prediction_status: "問卷初估",
          prediction_source: "survey",
          predicted_days: 30,
          last_order_date: null,
        },
        homeStats: { avg_order_interval_days: null, last_order_date: null },
      };
    case "preparing":
      return { gasStatus: { j_eligible: true, prediction_status: "可預測", last_order_date: null } };
    case "alert_early":
      return { gasStatus: { ...mkDevGasStatus({ lastOrder: -20, low: 28, high: 40, notify: 15 }), behavior_alert: { type: "early" as const, delta_days: -12 } } };
    case "alert_late":
      return { gasStatus: { ...mkDevGasStatus({ lastOrder: -50, low: 10, high: 22, notify: 5 }), behavior_alert: { type: "late" as const, delta_days: 18 } } };
    case "alert_irregular":
      return { gasStatus: { ...mkDevGasStatus({ lastOrder: -30, low: 20, high: 35, notify: 12 }), behavior_alert: { type: "irregular" as const } } };
    case "outcome_soft":
      return { gasStatus: mkDevGasStatus({ lastOrder: -40, low: 8, high: 20, notify: -1 }) };
    case "outcome_window":
      return { gasStatus: mkDevGasStatus({ lastOrder: -50, low: -2, high: 10, notify: -8 }) };
    case "outcome_past_h":
      return { gasStatus: mkDevGasStatus({ lastOrder: -70, low: -18, high: -5, notify: -25 }) };
    case "outcome_early":
      return { gasStatus: mkDevGasStatus({ lastOrder: -10, low: 25, high: 40, notify: 12 }) };
    case "outcome_late":
      return { gasStatus: mkDevGasStatus({ lastOrder: -80, low: -25, high: -8, notify: -35 }) };
    default:
      return null;
  }
};

const DEV_SURVEY_OPTIONS: { key: DevSurveyMode; label: string }[] = [
  { key: "real", label: "實際" },
  { key: "todo", label: "未填" },
  { key: "done", label: "已填" },
];

const DEV_SURVEY_QUICK_DAYS = [14, 30, 45, 90] as const;

const DevStateSwitcher = ({
  scenario,
  onScenario,
  survey,
  onSurvey,
  rangeFont,
  onRangeFont,
  outcomeForce,
  onOutcomeForce,
  coldHeaderShape,
  onColdHeaderShape,
  onQuickSubmitSurvey,
  quickSurveyBusy,
}: {
  scenario: DevScenarioKey;
  onScenario: (k: DevScenarioKey) => void;
  survey: DevSurveyMode;
  onSurvey: (m: DevSurveyMode) => void;
  rangeFont: IntervalRangeFontKey;
  onRangeFont: (k: IntervalRangeFontKey) => void;
  outcomeForce: DevOutcomeForce;
  onOutcomeForce: (k: DevOutcomeForce) => void;
  coldHeaderShape: DevColdHeaderShape;
  onColdHeaderShape: (k: DevColdHeaderShape) => void;
  /** 真實 POST gas-survey（測 predict_one）；非 mock */
  onQuickSubmitSurvey?: (surveyDays: number) => void;
  quickSurveyBusy?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const activeLabel = DEV_SCENARIO_LABELS.find((s) => s.key === scenario)?.label ?? "實際資料";
  const outcomeLabel =
    DEV_OUTCOME_OPTIONS.find((o) => o.key === outcomeForce)?.label ?? "自動";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "fixed",
          top: 10,
          right: 10,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 5,
          background: scenario === "real" && outcomeForce === "auto" ? "rgba(17,24,39,0.72)" : "#7C3AED",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          padding: "6px 12px",
          fontSize: 11,
          fontWeight: 700,
          fontFamily: "inherit",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          maxWidth: 200,
        }}>
        <RiFlaskLine size={13} color="#fff" />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {scenario === "real" && outcomeForce === "auto" ? "DEV" : `${activeLabel}·${outcomeLabel}`}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            top: 44,
            right: 10,
            zIndex: 9999,
            width: 260,
            maxHeight: "78vh",
            overflowY: "auto",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
            padding: 10,
          }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: 1, padding: "2px 6px 6px" }}>
            客戶狀態預覽
          </div>
          {DEV_SCENARIO_LABELS.map((s) => {
            const active = scenario === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  onScenario(s.key);
                  // 結果情境自動對齊 force，其餘回到 auto
                  if (s.key === "outcome_soft") onOutcomeForce("missed_soft");
                  else if (s.key === "outcome_window") onOutcomeForce("missed_in_window");
                  else if (s.key === "outcome_past_h") onOutcomeForce("missed_past_h");
                  else if (s.key === "outcome_early") onOutcomeForce("early_order");
                  else if (s.key === "outcome_late") onOutcomeForce("late_order");
                  else if (s.key.startsWith("alert_")) onOutcomeForce("none");
                  else if (s.key === "ai_ample") onOutcomeForce("none");
                  else onOutcomeForce("auto");
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: active ? "#F3EEFF" : "transparent",
                  border: "none",
                  borderRadius: 9,
                  padding: "7px 8px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: active ? "#7C3AED" : C.ink }}>
                  {s.label}
                </span>
                <span style={{ display: "block", fontSize: 10.5, color: C.muted, marginTop: 1 }}>{s.desc}</span>
              </button>
            );
          })}

          <div style={{ borderTop: "1px solid #F1F2F4", margin: "8px 0" }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: 1, padding: "0 6px 6px" }}>
            快速回答（強制預覽）
          </div>
          {DEV_OUTCOME_OPTIONS.map((o) => {
            const active = outcomeForce === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => {
                  onOutcomeForce(o.key);
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: active ? "#F3EEFF" : "transparent",
                  border: "none",
                  borderRadius: 9,
                  padding: "7px 8px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: active ? "#7C3AED" : C.ink }}>
                  {o.label}
                </span>
                <span style={{ display: "block", fontSize: 10.5, color: C.muted, marginTop: 1 }}>{o.desc}</span>
              </button>
            );
          })}

          <div style={{ borderTop: "1px solid #F1F2F4", margin: "8px 0" }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: 1, padding: "0 6px 6px" }}>
            問卷快捷（真實 API）
          </div>
          <div style={{ fontSize: 10, color: C.muted, padding: "0 6px 8px", lineHeight: 1.4 }}>
            一鍵組預設答案並 POST /gas-survey → predict_one。失敗會 toast（例如未綁 cis）。
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 4px 8px" }}>
            {DEV_SURVEY_QUICK_DAYS.map((days) => (
              <button
                key={days}
                type="button"
                disabled={quickSurveyBusy || !onQuickSubmitSurvey}
                onClick={() => onQuickSubmitSurvey?.(days)}
                style={{
                  flex: "1 1 40%",
                  padding: "8px 0",
                  borderRadius: 8,
                  fontSize: 11.5,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  color: "#fff",
                  background: quickSurveyBusy ? "#A78BFA" : "#7C3AED",
                  border: "none",
                  cursor: quickSurveyBusy ? "wait" : "pointer",
                  opacity: !onQuickSubmitSurvey ? 0.5 : 1,
                }}>
                {quickSurveyBusy ? "送出中…" : `${days} 天・送出`}
              </button>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #F1F2F4", margin: "8px 0" }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: 1, padding: "0 6px 6px" }}>
            問卷狀態
          </div>
          <div style={{ display: "flex", gap: 6, padding: "0 4px 4px" }}>
            {DEV_SURVEY_OPTIONS.map((o) => {
              const active = survey === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => onSurvey(o.key)}
                  style={{
                    flex: 1,
                    padding: "6px 0",
                    borderRadius: 8,
                    fontSize: 11.5,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    color: active ? "#fff" : C.ink,
                    background: active ? "#7C3AED" : "#F1F2F5",
                    border: "none",
                    cursor: "pointer",
                  }}>
                  {o.label}
                </button>
              );
            })}
          </div>

          <div style={{ borderTop: "1px solid #F1F2F4", margin: "8px 0" }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: 1, padding: "0 6px 6px" }}>
            冷啟動 Header 形狀
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 4px 6px" }}>
            {([{ key: "auto" as const, label: "自動" }, ...COLD_HEADER_SHAPE_OPTIONS.map((o) => ({ key: o.key, label: o.label }))]).map((o) => {
              const active = coldHeaderShape === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => onColdHeaderShape(o.key)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    color: active ? "#fff" : C.ink,
                    background: active ? "#7C3AED" : "#F1F2F5",
                    border: "none",
                    cursor: "pointer",
                  }}>
                  {o.label}
                </button>
              );
            })}
          </div>

          <div style={{ borderTop: "1px solid #F1F2F4", margin: "8px 0" }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: "#7C3AED", letterSpacing: 1, padding: "0 6px 6px" }}>
            區間字體
          </div>
          {INTERVAL_RANGE_FONT_OPTIONS.map((o) => {
            const active = rangeFont === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => onRangeFont(o.key)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: active ? "#F3EEFF" : "transparent",
                  border: "none",
                  borderRadius: 9,
                  padding: "7px 8px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: active ? "#7C3AED" : C.ink }}>
                  {o.label}
                </span>
                <span style={{ display: "block", fontSize: 10.5, color: C.muted, marginTop: 1 }}>{o.desc}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
};

export const HomePage = () => {
  const devSwitcherVisible = useDevSwitcherVisible();
  const navigate = useNavigate();
  // 沒配到就當新客戶 → 一律做「首次加入設定」（含自述既有但沒配到的 pendingReview；
  // 設定第一步的身分核對仍會問是我/不是我）。
  const { needIdentityCheck, notFound, setNotFound } = useKiosk();
  // ── 開發測試：狀態切換（production 不啟用）；需早於 needSetup 判斷 ──
  const [devScenario, setDevScenario] = useState<DevScenarioKey>("real");
  const [devSurvey, setDevSurvey] = useState<DevSurveyMode>("real");
  const [devColdHeaderShape, setDevColdHeaderShape] = useState<DevColdHeaderShape>("auto");
  const [devRangeFont, setDevRangeFont] = useState<IntervalRangeFontKey>("condensed");
  const [devOutcomeForce, setDevOutcomeForce] = useState<DevOutcomeForce>("auto");
  // DEV：join_setup 強制模擬「新註冊尚未完成加入設定」
  const needSetup =
    (IS_DEV && devScenario === "join_setup") || needIdentityCheck;
  const hideNotFoundForDev = IS_DEV && devScenario === "join_setup";
  const showNotFoundCard = notFound && !hideNotFoundForDev;
  // 練習「首次加入設定」情境：完成設定回到首頁後（needSetup 轉為 false、按鈕出現）
  // 幫「立即預約叫桶」加脈動框，提示客服「設定好了，可以開始訂購」。
  const practiceActive = usePracticeStore((s) => s.active);
  const practiceScenario = usePracticeStore((s) => s.scenario);
  const showOrderPulse = practiceActive && practiceScenario === "firstJoinSetup";
  const [gateOpen, setGateOpen] = useState(false);
  const [gateGoOrder, setGateGoOrder] = useState(false); // 完成後是否續進訂購（叫桶觸發 true；設定卡觸發 false）
  const goOrder = () => {
    track("entry_click", { entry_source: "home_cta" });
    if (needSetup) { setGateGoOrder(true); setGateOpen(true); return; } // 未完成加入設定 → 先做設定
    navigate("/gasShop/products");
  };
  const { chooseIdentity, setUserData } = useUser();
  const { cusJwtToken } = useCusAuth();
  const { setLoading } = useUtilsStore();
  const { openSheet } = useHomePageSheetStore();

  const [resData, setResData] = useState<any>(null);
  /** 首次 /home 回來前不渲染問卷／Hero，避免半透明 loading 透出錯誤畫面 */
  const [homeReady, setHomeReady] = useState(false);
  /** 有快取先畫時，背景靜默更新中（頂部細條，不用全螢幕鋼瓶） */
  const [homeRefreshing, setHomeRefreshing] = useState(false);
  const homeFetchSeq = useRef(0);
  /** 已載入的是哪個 context（含用記住的 id 先發的那次），身分回來後一致就不重打 */
  const loadedContextIdRef = useRef<string | null>(null);
  /** 用記住的 id 先發的那次失敗了 → 一律等正式身分再打 */
  const provisionalFailedRef = useRef(false);
  /** 用記住的 id 先發、還在路上的那個 context：/contexts 先回來時不要重複打同一支 */
  const provisionalInFlightRef = useRef<string | null>(null);
  /** provisional 失敗後用來重新觸發正式那一次 */
  const [homeFetchNonce, setHomeFetchNonce] = useState(0);

  // ── Inline 問卷狀態 ──
  const [localSurveyRecord, setLocalSurveyRecord] = useState<GasSurveyRecord | null>(null);
  /** 問卷已入庫但 predict_one 未寫入／失敗 → 顯示防呆 pending Hero */
  const [surveyPredictionPending, setSurveyPredictionPending] = useState(false);
  /** 問卷送出後轉場：等 home／預測結果就緒再關掉（不再顯示「已收到」橫幅） */
  const [surveyTransitioning, setSurveyTransitioning] = useState(false);
  /** 「約每 N 天」主動更新問卷 modal */
  const [surveyUpdateOpen, setSurveyUpdateOpen] = useState(false);

  useEffect(() => {
    if (!surveyPredictionPending) return;
    const days = resData?.home_gas_status?.predicted_days;
    const ready =
      (typeof days === "number" && days > 0) ||
      (typeof days === "string" && Number(days) > 0);
    if (ready) setSurveyPredictionPending(false);
  }, [surveyPredictionPending, resData?.home_gas_status?.predicted_days]);

  /**
   * @param opts.contextId  指定要打哪個 context（開機時身分還沒回來，先用 localStorage 記住的 id）
   * @param opts.provisional 用記住的 id 先發的那一次。失敗時不做任何收尾（不設 homeReady、
   *                         不記 401 token），單純讓位給 /contexts 回來後的正式那一次，
   *                         避免記錯 id 時閃一張冷啟動卡。
   */
  const fetchHomeData = async (opts?: { silent?: boolean; contextId?: string | number; provisional?: boolean }): Promise<any | null> => {
    const contextId = opts?.contextId ?? chooseIdentity?.context_id;
    const seq = ++homeFetchSeq.current;
    // 此 token 先前已被 /home 判為未綁定(401) → 不重打，避免 log 噪音
    if (cusJwtToken && homeUnauthTokens.has(cusJwtToken)) {
      setHomeReady(true);
      setHomeRefreshing(false);
      setLoading(false);
      return null;
    }
    setHomeRefreshing(true); //-- 頁內細進度條；無快取時 homeReady 仍 false，會顯示頁內轉圈
    try {
      const res = await api.get("/app-api/customer/home", {
        headers: {
          Authorization: `Bearer ${cusJwtToken}`,
          "X-Context": contextId != null ? `context:${contextId}` : buildXContextHeader(chooseIdentity),
        },
      });
      if (seq !== homeFetchSeq.current) return null;
      loadedContextIdRef.current = contextId != null ? String(contextId) : null;
      const { data } = res.data;
      setResData(data);
      writeHomeCache(contextId, data);
      setUserData({
        name: data?.customer?.customer_name,
        phone: data?.customer?.main_phone,
      });
      return data;
    } catch (error) {
      if (seq !== homeFetchSeq.current) return null;
      // 記住的 id 先發的那一次失敗（id 已失效／不屬於此帳號）：不做任何收尾，
      // 等 /contexts 回來後用正式身分再打一次。
      if (opts?.provisional) {
        provisionalFailedRef.current = true;
        provisionalInFlightRef.current = null;
        setHomeRefreshing(false);
        console.log("home provisional err", error);
        setHomeFetchNonce((n) => n + 1); //-- 身分已到位的話，讓下面那支正式的補打
        return null;
      }
      // 未綁定客戶 → 401：記住此 token，之後同一 token 不再打 /home（減少 log 噪音）
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 && cusJwtToken) {
        homeUnauthTokens.add(cusJwtToken);
        clearHomeCache(contextId);
        setResData(null);
      } else if (!opts?.silent) {
        // 冷啟動失敗才清空；silent 更新失敗保留舊畫面
        setResData(null);
      }
      console.log("home err", error);
      return null;
    } finally {
      if (seq === homeFetchSeq.current && !(opts?.provisional && provisionalFailedRef.current)) {
        setHomeReady(true);
        setHomeRefreshing(false);
        // 冷啟動關品牌 loading；有快取 silent 路徑也要關掉 login 留下的轉圈
        setLoading(false);
      }
    }
  };

  // 這裡原本有一支 fetchIdentities()：拿到 token 就打 GET /customer/contexts，但
  // successCallback 是空的、也沒接 setLoading，回應完全被丟棄。同一份身分清單由
  // IdentitySwitchMenu 自己抓並實際使用，所以這支純屬重複請求，已移除。
  // （prod 3 天實測 /customer/contexts 280 次 vs /customer/home 106 次，比例 2.6 倍。）

  // 換身分／進頁：有 session 快取就先畫（SWR），沒有才顯示頁內轉圈
  useLayoutEffect(() => {
    const contextId = chooseIdentity?.context_id;
    if (!contextId) {
      setResData(null);
      setHomeReady(false);
      setHomeRefreshing(false);
      return;
    }
    // 身分回來前用記住的 id 已經把同一個 context 抓好了 → 保留畫面，別退回未就緒
    if (loadedContextIdRef.current === String(contextId)) return;
    const cached = readHomeCache(contextId);
    if (cached) {
      setResData(cached);
      setHomeReady(true);
      setLoading(false);
    } else {
      setResData(null);
      setHomeReady(false);
    }
  }, [chooseIdentity?.context_id, setLoading]);

  // 資料就緒 → 關掉開機（LineLoginProvider）或其他流程殘留的全域遮罩。首頁只關不開。
  useEffect(() => {
    if (homeReady) setLoading(false);
  }, [homeReady, setLoading]);

  // contexts 空／失敗時不會有 chooseIdentity，/home 也不會被打；逾時放行避免永遠轉圈
  useEffect(() => {
    if (!cusJwtToken || homeReady || chooseIdentity?.context_id) return;
    const t = window.setTimeout(() => {
      setHomeReady(true);
      setLoading(false);
    }, 10000);
    return () => window.clearTimeout(t);
  }, [cusJwtToken, chooseIdentity?.context_id, homeReady, setLoading]);

  // 開機加速：身分（/customer/contexts）還沒回來時，先用 localStorage 記住的 context_id
  // 把 /home 發出去，兩支請求平行跑。記錯就讓下面那支正式的覆蓋，畫面不會提早畫出舊資料。
  useEffect(() => {
    if (!cusJwtToken || chooseIdentity?.context_id) return;
    if (loadedContextIdRef.current || provisionalFailedRef.current) return;
    const remembered = readLastContextId();
    if (!remembered) return;
    provisionalInFlightRef.current = remembered;
    fetchHomeData({ contextId: remembered, provisional: true }).finally(() => {
      if (provisionalInFlightRef.current === remembered) provisionalInFlightRef.current = null;
    });
  }, [cusJwtToken, chooseIdentity?.context_id]);

  useEffect(() => {
    if (!cusJwtToken || !chooseIdentity?.context_id) return;
    // 記住的 id 猜對了 → 不重複打：資料已到（loadedContextIdRef）或還在路上（provisionalInFlightRef）。
    // 還在路上那支若失敗，會 bump homeFetchNonce 讓這裡補打。
    const id = String(chooseIdentity.context_id);
    if (loadedContextIdRef.current === id) return;
    if (provisionalInFlightRef.current === id) return;
    const hasCache = readHomeCache(chooseIdentity.context_id) != null;
    fetchHomeData({ silent: hasCache });
  }, [cusJwtToken, chooseIdentity?.context_id, homeFetchNonce]);

  /** 問卷完成：必須 API 成功後才寫本機／切完成態（禁止樂觀更新） */
  const handleInlineSurveyComplete = async (record: GasSurveyRecord) => {
    setSurveyTransitioning(true);

    const hasPredictDays = (data: any) => {
      const d = data?.home_gas_status?.predicted_days;
      return typeof d === "number" && Number.isFinite(d) && d > 0;
    };

    const waitForPredictResult = async (maxMs = 12000): Promise<void> => {
      const started = Date.now();
      let delay = 400;
      while (Date.now() - started < maxMs) {
        const data = await fetchHomeData({ silent: true });
        if (hasPredictDays(data)) return;
        await new Promise((r) => window.setTimeout(r, delay));
        delay = Math.min(delay + 300, 1600);
      }
      await fetchHomeData({ silent: true });
    };

    const xContext = buildXContextHeader(chooseIdentity);
    try {
      if (!cusJwtToken || !xContext) {
        throw new Error("缺少登入或帳戶上下文，無法送出問卷");
      }
      const submitRes = await postGasSurvey({
        token: cusJwtToken,
        xContext,
        record,
      });
      saveGasSurvey(chooseIdentity?.context_id, record);
      setLocalSurveyRecord(record);

      const fromSubmit = submitRes?.home_gas_status?.predicted_days;
      const hasSubmitDays =
        (typeof fromSubmit === "number" && Number.isFinite(fromSubmit) && fromSubmit > 0) ||
        (typeof fromSubmit === "string" && Number(fromSubmit) > 0);
      const predictNotReady = Boolean(
        submitRes?.predict_error || submitRes?.prediction_pending || !hasSubmitDays,
      );

      if (hasSubmitDays) {
        setSurveyPredictionPending(false);
        toast.success(`問卷已送出・約每 ${Math.round(Number(fromSubmit))} 天`);
        await fetchHomeData({ silent: true });
      } else if (predictNotReady) {
        setSurveyPredictionPending(true);
        toast("已收到資料，預測將在不久後準備完成", { icon: "⏳" });
        if (submitRes?.prediction_pending) {
          await waitForPredictResult();
        } else {
          await fetchHomeData({ silent: true });
        }
      } else {
        setSurveyPredictionPending(false);
        toast.success("問卷已送出");
        await fetchHomeData({ silent: true });
      }
    } catch (err) {
      console.warn("gas-survey submit failed; not marking complete", err);
      toast.error(toToastMessage((err as any)?.response?.data?.message, "問卷送出失敗，請確認已完成綁定後再試"));
      throw err;
    } finally {
      setSurveyTransitioning(false);
    }
  };

  const handleSurveyUpdateComplete = async (record: GasSurveyRecord) => {
    try {
      await handleInlineSurveyComplete(record);
      setSurveyUpdateOpen(false);
    } catch {
      /* toast 已在 handleInlineSurveyComplete；modal 保持開啟可再試 */
    }
  };

  const [quickSurveyBusy, setQuickSurveyBusy] = useState(false);
  const handleDevQuickSubmitSurvey = async (surveyDays: number) => {
    if (quickSurveyBusy || surveyTransitioning) return;
    setQuickSurveyBusy(true);
    setDevScenario("real");
    setDevSurvey("real");
    try {
      await handleInlineSurveyComplete(buildDevDefaultSurveyRecord({ survey_days: surveyDays }));
    } catch {
      /* toast 已在 handleInlineSurveyComplete */
    } finally {
      setQuickSurveyBusy(false);
    }
  };

  // ── 開發測試：狀態切換（其餘 state；scenario 等已提前宣告）──
  const [quickAskDismissed, setQuickAskDismissed] = useState(false);
  const [quickAskNonce, setQuickAskNonce] = useState(0);

  const previewQuickAsk = (force: DevOutcomeForce) => {
    setDevOutcomeForce(force);
    if (force !== "auto" && force !== "none") {
      setQuickAskDismissed(false);
      setQuickAskNonce((n) => n + 1);
    }
  };
  const devMock = IS_DEV && devScenario !== "real" ? buildDevMock(devScenario) : null;

  // ── AI 智慧預測（區間 v2）：耗完區間 + 商業提醒分層 ──
  const gasStatus = devMock
    ? devMock.gasStatus
    : ((resData?.home_gas_status ?? null) as HomeGasStatusInterval | null);

  // 平均叫桶（相鄰訂單間隔平均）；資料來自 home_stats
  const homeStats = devMock?.homeStats ?? resData?.home_stats;
  const avgDays: number | null = homeStats?.avg_order_interval_days ?? null;
  const lastOrderDate: string | null = homeStats?.last_order_date ?? null;
  const hasRecord = avgDays !== null;
  /** 有真實訂單錨點才允許日曆區間（0 筆只能顯示週期初估） */
  const hasRealOrder = Boolean(lastOrderDate);

  // 暖啟動：不啟用「自動安排」取代 CTA；有區間一律走預約畫面
  const ENABLE_AUTO_DISPATCH_UI = false;

  // 日曆區間只給「有真實訂單錨點」的暖啟動。
  // survey coldstart 也會寫 L/H，但 0 筆時必須走「約每 N 天」，不可被假區間蓋掉。
  const hasIntervalDates = Boolean(
    gasStatus?.dispatch_lower_date ||
      gasStatus?.depletion_low ||
      gasStatus?.depletion_high ||
      gasStatus?.observe_upper_date,
  );
  /**
   * 方案 6 鬆 G（只藏不縮）：model 列需 CV&lt;0.4 且中位週期&lt;120 才顯示完整 L～H。
   * - survey：不受此閘
   * - cv／週期缺值：相容舊 API（尚未部署欄位時仍顯示）
   */
  const INTERVAL_DISPLAY_MAX_CV = 0.4;
  const INTERVAL_DISPLAY_MAX_CYCLE_DAYS = 120;
  const passesIntervalDisplayGate = (() => {
    if (!gasStatus) return false;
    if (gasStatus.prediction_source === "survey") return true;
    const cv = gasStatus.interval_cv;
    const cycle = gasStatus.rolling_median_interval_days;
    const hasCv = typeof cv === "number" && Number.isFinite(cv);
    const hasCycle = typeof cycle === "number" && Number.isFinite(cycle);
    if (!hasCv || !hasCycle) return true; // 舊 API 相容
    return cv < INTERVAL_DISPLAY_MAX_CV && cycle < INTERVAL_DISPLAY_MAX_CYCLE_DAYS;
  })();
  const intervalEligible =
    !!gasStatus &&
    gasStatus.prediction_status !== "無法預測" &&
    hasRealOrder &&
    passesIntervalDisplayGate &&
    (hasIntervalDates ||
      gasStatus.auto_eligible === true ||
      gasStatus.j_eligible === true);
  const noData = !intervalEligible;
  const intervalVm = (() => {
    if (!intervalEligible || !gasStatus) return null;
    const vm = buildIntervalGasViewModel(gasStatus);
    if (!vm) return null;
    if (!ENABLE_AUTO_DISPATCH_UI && vm.showAutoDispatchMessage) {
      return { ...vm, showAutoDispatchMessage: false };
    }
    return vm;
  })();

  // 天數 count-up（ease-out cubic）：進場時從 0 跑到實際值，較有質感
  const [animDays, setAnimDays] = useState(0);
  useEffect(() => {
    if (avgDays == null) {
      setAnimDays(0);
      return;
    }
    let raf = 0;
    let start: number | undefined;
    const tick = (t: number) => {
      if (start === undefined) start = t;
      const p = Math.min((t - start) / 900, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setAnimDays(Math.round(avgDays * e));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [avgDays]);

  const vendor = resData?.vendor as { supplier_name?: string; phone?: string; is_open_now?: boolean } | null;

  // 用氣習慣問卷：未填或更新週期到了，首頁直接顯示題目（不需點擊任何按鈕）。
  // localSurveyRecord：本次 session 剛 inline 填完的紀錄（優先於 localStorage）。
  const realSurveyRecord = getGasSurvey(chooseIdentity?.context_id);
  const effectiveSurveyRecord = localSurveyRecord ?? realSurveyRecord;
  // survey_zero / survey_pending / join_setup 情境強制已填，方便本地預覽
  const surveyDone =
    (IS_DEV &&
      (devScenario === "survey_zero" ||
        devScenario === "survey_pending" ||
        devScenario === "join_setup")) ||
    (IS_DEV && devSurvey !== "real" ? devSurvey === "done" : Boolean(effectiveSurveyRecord));
  // dev 切「已填」但 localStorage 沒實際紀錄時，用固定樣本讓問卷版預測可預覽
  const surveyRecord: GasSurveyRecord | null = surveyDone
    ? (effectiveSurveyRecord ??
      (IS_DEV && devScenario !== "survey_pending"
        ? { survey_days: 30, cylinder_kg: 20, observed_interval_days: null, completed_at: new Date().toISOString() }
        : null))
    : null;

  const apiSurveyPrompt = resData?.survey_prompt as
    | {
        needed?: boolean;
        reason?: "never" | "refresh" | null;
        completed_at?: string | null;
        /** 後端 gas_survey.survey_days；無 predictions／無 localStorage 時作「約每 N 天」後援 */
        survey_days?: number | null;
      }
    | null
    | undefined;
  const apiCycleFeedback = resData?.cycle_feedback as
    | {
        kind?: CycleOutcomeKind | null;
        cycle_anchor?: string | null;
        should_show?: boolean;
        surface?: "inline" | "sheet" | null;
        paused?: boolean;
      }
    | null
    | undefined;

  const surveyNeed = (() => {
    if (
      IS_DEV &&
      (devScenario === "survey_zero" ||
        devScenario === "survey_pending" ||
        devScenario === "join_setup")
    ) {
      return { needed: false as const, reason: null };
    }
    if (IS_DEV && devSurvey === "todo") {
      if (localSurveyRecord) return { needed: false as const, reason: null };
      return { needed: true as const, reason: "never" as const };
    }
    if (IS_DEV && devSurvey === "done") {
      return { needed: false as const, reason: null };
    }
    // 首次 /home 未回前不依 localStorage 顯示問卷（避免 loading 底下透出問卷）
    if (!homeReady) {
      return { needed: false as const, reason: null };
    }
    // 後端權威；無欄位時 fallback localStorage（offline／舊 API）
    if (apiSurveyPrompt && typeof apiSurveyPrompt.needed === "boolean") {
      return {
        needed: apiSurveyPrompt.needed,
        reason: (apiSurveyPrompt.reason ?? null) as "never" | "refresh" | null,
      };
    }
    return needsGasSurvey(effectiveSurveyRecord);
  })();

  const showSurveyForm = surveyNeed.needed || surveyTransitioning;

  // 1–3 筆：問卷可用真實 last_order 推日曆區間
  // 0 筆：不建 surveyVm（改走 SurveyCycleEstimateHero）
  const surveyVm = (() => {
    if (!noData || !surveyRecord || !hasRealOrder) return null;
    const { predictedDays } = estimateCycleFromSurvey(surveyRecord);
    const anchor = resolveSurveyAnchorDate(surveyRecord, homeStats?.last_order_date);
    return buildIntervalGasViewModel({
      j_eligible: true,
      prediction_status: "問卷初估",
      predicted_days: predictedDays,
      last_order_date: anchor,
    });
  })();

  const effectiveVm = intervalVm ?? surveyVm;

  const apiSurveyDays = (() => {
    const n = apiSurveyPrompt?.survey_days;
    if (typeof n === "number" && Number.isFinite(n) && n > 0) return Math.round(n);
    if (typeof n === "string" && Number(n) > 0) return Math.round(Number(n));
    return null;
  })();

  const surveyCycleDays = (() => {
    // 優先用 lpg-predict 寫回的正式天數（survey 列）
    if (gasStatus?.prediction_source === "survey") {
      const n = gasStatus?.predicted_days;
      if (typeof n === "number" && n > 0) return Math.round(n);
      if (typeof n === "string" && Number(n) > 0) return Math.round(Number(n));
    }
    if (surveyRecord) return estimateCycleFromSurvey(surveyRecord).predictedDays;
    // 問卷已寫入 DB、但 predict_one／PM 尚未就緒、且 localStorage 也沒有：吃 home.survey_prompt.survey_days
    if (apiSurveyDays != null) return apiSurveyDays;
    const n = gasStatus?.predicted_days;
    if (typeof n === "number" && n > 0) return Math.round(n);
    if (typeof n === "string" && Number(n) > 0) return Math.round(Number(n));
    return null;
  })();

  /** 無區間預測時顯示「約每 N 天」（含：0 筆；或有單但 PM／區間尚未就緒） */
  const forceDevSurveyPending = IS_DEV && devScenario === "survey_pending";
  const showSurveyCycleEstimate =
    !effectiveVm &&
    !showSurveyForm &&
    !surveyPredictionPending &&
    !forceDevSurveyPending &&
    surveyCycleDays != null &&
    surveyCycleDays > 0 &&
    (Boolean(surveyRecord) ||
      gasStatus?.prediction_source === "survey" ||
      // 後端已標問卷完成（needed=false）且有 survey_days → 即使無 PM／無 local 也要出數值
      (apiSurveyPrompt?.needed === false && apiSurveyDays != null));

  // 問卷已完成但尚無數值（predict_one 失敗／尚未寫入）：防呆，避免空白冷啟動
  const showSurveyPredictionPending =
    forceDevSurveyPending ||
    (!effectiveVm &&
      !showSurveyForm &&
      !showSurveyCycleEstimate &&
      (surveyPredictionPending ||
        ((apiSurveyPrompt?.needed === false || Boolean(surveyRecord)) &&
          (surveyCycleDays == null || surveyCycleDays <= 0))));

  /** true = 問卷初估（後端 survey 或前端 fallback） */
  const isSurveyBased =
    gasStatus?.prediction_source === "survey" || (!intervalVm && !!surveyVm);
  const showColdHero = noData && !surveyVm && !showSurveyCycleEstimate && !showSurveyPredictionPending;
  const isColdNew = showColdHero && !hasRecord;

  const devForceOutcome =
    IS_DEV && devOutcomeForce !== "auto"
      ? (devOutcomeForce as CycleOutcomeKind | "none")
      : null;

  const outcomeKind = (() => {
    if (devForceOutcome === "none") return null;
    if (devForceOutcome) return devForceOutcome;
    if (apiCycleFeedback?.kind) return apiCycleFeedback.kind;
    // 舊 API fallback：本地推導
    if (!effectiveVm) return null;
    return resolveCycleOutcomeKind({
      notifyStandardDate:
        gasStatus?.notify_standard_date ??
        effectiveVm.notifyStages.find((s) => s.key === "standard")?.date,
      dispatchLowerDate: gasStatus?.dispatch_lower_date ?? effectiveVm.dispatchByDate,
      observeUpperDate: gasStatus?.observe_upper_date ?? effectiveVm.observeUpperDate,
    });
  })();

  const outcomeAnchor =
    apiCycleFeedback?.cycle_anchor ??
    gasStatus?.last_order_date ??
    gasStatus?.notify_standard_date ??
    lastOrderDate ??
    getTodayIso();

  const showOutcomeCard = (() => {
    // DEV 強制預覽：不需真實訂單／effectiveVm，方便模擬浮層動畫
    if (devForceOutcome && devForceOutcome !== "none") {
      return !quickAskDismissed;
    }
    // 0 筆無真實訂單：不顯示逾期／週期問答（沒有可信錨點）
    if (!hasRealOrder) return false;
    if (!outcomeKind || !effectiveVm || quickAskDismissed) return false;
    if (apiCycleFeedback && typeof apiCycleFeedback.should_show === "boolean") {
      return apiCycleFeedback.should_show;
    }
    return true;
  })();

  const outcomeSurface = (() => {
    if (devForceOutcome && devForceOutcome !== "none") {
      return isUrgentCycleOutcome(devForceOutcome) ? "sheet" : "inline";
    }
    return (
      apiCycleFeedback?.surface ??
      (outcomeKind && isUrgentCycleOutcome(outcomeKind) ? "sheet" : "inline")
    );
  })();
  const showUrgentSheet = Boolean(showOutcomeCard && outcomeKind && outcomeSurface === "sheet");
  const showInlineAsk = Boolean(showOutcomeCard && outcomeKind && outcomeSurface === "inline");

  useEffect(() => {
    if (!outcomeKind) {
      setQuickAskDismissed(false);
      return;
    }
    if (IS_DEV && devOutcomeForce !== "auto" && devOutcomeForce !== "none") {
      clearCycleOutcomeAck(chooseIdentity?.context_id, outcomeKind, outcomeAnchor);
      clearCycleNotifyPause(chooseIdentity?.context_id, outcomeAnchor);
      setQuickAskDismissed(false);
      return;
    }
    // 後端 should_show 已含 pause／當日 ack；session dismiss 仍尊重
    if (apiCycleFeedback && apiCycleFeedback.should_show === false) {
      setQuickAskDismissed(true);
      return;
    }
    setQuickAskDismissed(false);
  }, [
    outcomeKind,
    outcomeAnchor,
    chooseIdentity?.context_id,
    devOutcomeForce,
    apiCycleFeedback?.should_show,
  ]);

  const submitOutcomeAnswer = async (chip: string, note?: string) => {
    const xContext = buildXContextHeader(chooseIdentity);
    if (!cusJwtToken || !xContext || !outcomeKind) return;
    await postCycleFeedback({
      token: cusJwtToken,
      xContext,
      kind: outcomeKind,
      chipKey: chip,
      note,
      cycleAnchor: outcomeAnchor,
    });
    await fetchHomeData();
  };

  const submitOutcomePause = async (reason: string, note?: string) => {
    const xContext = buildXContextHeader(chooseIdentity);
    if (!cusJwtToken || !xContext || !outcomeKind) return;
    await postCyclePause({
      token: cusJwtToken,
      xContext,
      reason,
      note,
      kind: outcomeKind,
      cycleAnchor: outcomeAnchor,
    });
    await fetchHomeData();
  };

  const aiLow = effectiveVm?.stockStatus === "建議補充" || effectiveVm?.stockStatus === "偏低";
  const tankFill = effectiveVm?.tankFillPercent ?? 65;
  // 正式樣式：區間字體＝海報・窄體（Barlow Condensed）
  // 全站字體統一為粉圓後，大數字也回到同一套字（"condensed"／"bebas" 是海報體，
  // 留在 DEV 切換裡備用，見 intervalRangeFont.ts）。
  const rangeFont: IntervalRangeFontKey = "default";
  const ctaColor = C.blue;
  const ctaSubtitle = "線上預約，免通話";

  // /home 未回：頁內轉圈。開機時全域遮罩仍蓋在上面（由 LineLoginProvider 開啟），
  // 兩者樣式一致，接手時看不出換過。
  //
  // 這裡刻意「不」再要求 chooseIdentity?.context_id：身分只要有一個 frame 還沒補上
  //（換身分、contexts 尚未回、跨頁重掛載），就會落到下面正常渲染，而那時 resData 仍是
  // null → 先閃一張「智慧叫桶提醒設定」冷啟動卡，身分補上後又轉圈、最後才跳出預測結果，
  // 使用者看到的就是「設定 → loading → 結果」三段跳。沒有 /home 資料時一律先轉圈。
  // 逾時保險仍在（見上面 10 秒的 effect），身分始終拿不到也不會永遠卡住。
  if (cusJwtToken && !homeReady) {
    return <HomeSkeleton />;
  }

  return (
    <div
      className="home-scroll"
      style={{
        width: "100%",
        height: "100%",
        fontFamily: "var(--font-sans)",
        color: C.ink,
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        background: C.page,
        scrollbarWidth: "none",
        msOverflowStyle: "none" as any,
        paddingBottom: showUrgentSheet ? 220 : 0,
      }}>
      {/* 有快取先畫時：背景更新用頂部細條，不再蓋全螢幕鋼瓶 */}
      {homeRefreshing && (
        <div
          aria-hidden
          style={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 50,
            overflow: "hidden",
            background: "rgba(59,130,246,0.12)",
          }}>
          <div
            style={{
              height: "100%",
              width: "40%",
              background: "linear-gradient(90deg, transparent, #3B82F6, transparent)",
              animation: "homeRefreshSlide 1.1s ease-in-out infinite",
            }}
          />
        </div>
      )}
      <style>{`
        @keyframes homeRefreshSlide {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(320%); }
        }
        .home-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
        @keyframes ctaNudge { 0%,100% { transform:translateX(0); } 50% { transform:translateX(5px); } }
        /* AI 預測：Hero + CTA 區塊；鋼瓶 bottom 貼齊 CTA 底部 */
        .ai-hero-cta-zone { position: relative; }
        .ai-cta-sheet-lip {
          margin-top: -86px;
          background: #f8fafc;
          border-radius: 24px 24px 0 0;
          padding: 24px 18px 0;
          position: relative;
          /* z-index 要比下面那張白 sheet 高。兩者原本都是 1，而白 sheet 在 DOM 後面，
             於是把 CTA 的接觸影整條蓋掉——按鈕看起來就像被裁在區塊邊界上。
             按鈕本身沒有被裁（實測紅色像素到得了它自己的 rect 底部），被吃掉的是影子。 */
          z-index: 2;
        }
        .ai-card-stack { width: 100%; }
        .ai-hero-text {
          max-width: calc(100% - 152px);
          padding-right: 2px;
        }
        .ai-cta-clearance { margin-right: 88px; }
        .ai-glass-tank {
          position: absolute;
          right: -30px;
          bottom: 0;
          width: 110px;
          height: 310px;
          z-index: 30;
          pointer-events: none;
          display: flex;
          align-items: flex-end;
        }
        .ai-glass-tank svg { display: block; }
        /* 鋼瓶上方：手繪感對話框（飽滿圓角＋右下尖尾巴對準鋼瓶） */
        .ai-tank-cloud {
          position: absolute;
          left: 46%;
          top: 16%;
          transform: translate(-50%, -100%);
          z-index: 31;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .ai-tank-cloud .bubble {
          position: relative;
          z-index: 2;
          width: 92px;
          height: 52px;
          transform: translateX(-14px) translateY(-8px) rotate(-5deg);
          animation: doodleFloat 3.2s ease-in-out infinite;
          filter: drop-shadow(0 5px 12px rgba(15, 40, 80, 0.2));
        }
        .ai-tank-cloud .bubble-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .ai-tank-cloud .bubble-label {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13.5px;
          font-weight: 800;
          letter-spacing: 0.02em;
        }
        @keyframes doodleFloat {
          0%, 100% { transform: translateX(-14px) translateY(-8px) rotate(-5deg) scale(1); }
          50% { transform: translateX(-12px) translateY(-14px) rotate(-2deg) scale(1.03); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ai-tank-cloud .bubble { animation: none; }
        }
        .depletion-range-display {
          display: flex;
          flex-wrap: nowrap;
          align-items: baseline;
          column-gap: 0;
          max-width: 100%;
          white-space: nowrap;
        }
        .dispatch-hint-line { max-width: calc(100% - 8px); }
        @media (max-width: 390px) {
          .ai-hero-text { max-width: calc(100% - 80px); }
          .ai-cta-clearance { margin-right: 80px; }
          .ai-glass-tank { width: 100px; height: 286px; right: -26px; }
        }
        @media (max-width: 360px) {
          .ai-hero-text { max-width: calc(100% - 114px); }
          .ai-cta-clearance { margin-right: 72px; }
          .ai-glass-tank { width: 92px; height: 260px; right: -24px; }
          .depletion-range-display { letter-spacing: -0.02em; }
          .depletion-range-sep { padding: 0 2px !important; }
          .dispatch-hint-line { font-size: 14px; line-height: 1.35; }
        }
        @media (max-width: 340px) {
          .ai-hero-text { max-width: calc(100% - 102px); }
          .ai-cta-clearance { margin-right: 66px; }
          .ai-glass-tank { width: 82px; height: 238px; right: -20px; }
          .dispatch-hint-line { font-size: 13px; }
        }
        /* 練習首次設定完成 → 首頁訂購按鈕脈動框「可以訂購了」 */
        @keyframes ctaCanOrder {
          0%   { box-shadow: 0 0 0 0 rgba(33,150,243,0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(33,150,243,0); }
          100% { box-shadow: 0 0 0 0 rgba(33,150,243,0); }
        }
        .cta-canorder { animation: ctaCanOrder 1.6s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) { .cta-canorder { animation: none; box-shadow: 0 0 0 3px rgba(33,150,243,0.45); } }
        /* AI 預測 CTA：右側留白給鋼瓶（鋼瓶縮小後按鈕加寬），窄螢幕縮小留白避免按鈕過窄 */
        .ai-cta-wrap { margin-right: 112px; }
        @media (max-width: 390px) { .ai-cta-wrap { margin-right: 98px; } }
        @media (max-width: 340px) { .ai-cta-wrap { margin-right: 72px; } }
      `}</style>

      {/* 開發測試面板：本地預覽情境切換（預設隱藏，改 SHOW_DEV_SWITCHER=true 才顯示）*/}
      {devSwitcherVisible && (
        <DevStateSwitcher
          scenario={devScenario}
          onScenario={setDevScenario}
          survey={devSurvey}
          onSurvey={setDevSurvey}
          rangeFont={devRangeFont}
          onRangeFont={setDevRangeFont}
          outcomeForce={devOutcomeForce}
          onOutcomeForce={previewQuickAsk}
          coldHeaderShape={devColdHeaderShape}
          onColdHeaderShape={setDevColdHeaderShape}
          onQuickSubmitSurvey={handleDevQuickSubmitSurvey}
          quickSurveyBusy={quickSurveyBusy || surveyTransitioning}
        />
      )}

      {showSurveyCycleEstimate && surveyCycleDays != null ? (
        <SurveyCycleEstimateHero
          predictedDays={surveyCycleDays}
          onWhyNoPrediction={() => openSheet(NO_PREDICTION_SHEET)}
          onUpdateSurvey={() => setSurveyUpdateOpen(true)}
        />
      ) : showSurveyPredictionPending ? (
        <SurveyPredictionPendingHero onWhyNoPrediction={() => openSheet(NO_PREDICTION_SHEET)} />
      ) : showColdHero ? (
        !hasRecord ? (
          <ColdStartNewHero
            shape={devColdHeaderShape}
            onWhyNoPrediction={() => openSheet(NO_PREDICTION_SHEET)}>
            {showSurveyForm && (
              <InlineSurveyCard
                spotlight
                enableDevQuickFill={devSwitcherVisible}
                onComplete={handleInlineSurveyComplete}
                reason={surveyNeed.reason}
                initialRecord={surveyNeed.reason === "refresh" ? surveyRecord : null}
              />
            )}
          </ColdStartNewHero>
        ) : (
        /* ── 漸層 Hero（有叫桶平均、尚無 AI 預測）── */
        <div
          style={{
            background: `linear-gradient(to bottom, ${C.blue}, ${C.blueDark})`,
            padding: "26px 22px 44px",
            color: "#fff",
          }}>
          {/* 帳戶選擇（左）＋ 此帳戶成員（右），對齊瓦斯訂購頁 */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40, minHeight: 40 }}>
            <IdentitySwitchMenu />
            <MemberCircleList />
          </div>

          {/* 叫桶週期 */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 17, opacity: 0.92, marginBottom: 24 }}>
              <RiLineChartLine size={19} color="#fff" />
              <span>叫桶週期</span>
            </div>

            <>
              <div style={{ fontSize: 27, lineHeight: 1 }}>平均每</div>
              <div
                style={{
                  fontFamily: DAY_NUMBER_FONT,
                  fontSize: 100,
                  fontWeight: 600,
                  lineHeight: 1,
                  // Fredoka 的字腔比粉圓緊，-3 會擠在一起
                  letterSpacing: -1.5,
                  margin: "8px 0",
                  color: "#fff",
                  textShadow: "0 10px 26px rgba(0,40,90,0.28)",
                  fontVariantNumeric: "tabular-nums",
                }}>
                {animDays}
              </div>
              <div style={{ fontSize: 27, marginBottom: 26 }}>天叫一次</div>
            </>

            <button
              type="button"
              onClick={() => openSheet(NO_PREDICTION_SHEET)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                background: "rgba(255,255,255,0.18)",
                border: "none",
                color: "#fff",
                fontSize: 15,
                padding: "9px 18px",
                borderRadius: 20,
                cursor: "pointer",
              }}>
              <RiSparkling2Line size={16} color="#fff" />
              <span>AI 學習中</span>
              <RiQuestionLine size={16} color="rgba(255,255,255,0.8)" />
            </button>
          </div>

          {/* 上次叫桶 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 13,
              background: "rgba(255,255,255,0.18)",
              border: "0.5px solid rgba(255,255,255,0.25)",
              borderRadius: 15,
              padding: "16px 18px",
            }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
              <RiCalendarEventLine size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>上次叫桶</div>
              <div style={{ fontSize: 22, fontWeight: 500, marginTop: 2 }}>{toMonthDay(lastOrderDate)}</div>
            </div>
          </div>
        </div>
        )
      ) : !effectiveVm ? (
        <div
          style={{
            background: `linear-gradient(to bottom, ${C.blue}, ${C.blueDark})`,
            padding: "26px 22px 44px",
            color: "#fff",
          }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, minHeight: 40 }}>
            <IdentitySwitchMenu />
            <MemberCircleList />
          </div>
          <div style={{ fontSize: 16, opacity: 0.9 }}>預測資料整理中，請稍後再查看</div>
        </div>
      ) : (
        /* ── AI 智慧預測 Hero（區間 v2；冷啟動已填問卷時為問卷初估）── */
        <div className="ai-hero-cta-zone">
          <div
            style={{
              background: effectiveVm!.heroGradient,
              position: "relative",
              paddingBottom: 100,
              color: "#fff",
              transition: "background 0.6s ease",
            }}>
            <Grain o={0.03} />

            {/* 帳戶選擇（左）＋ 此帳戶成員（右）*/}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "26px 22px 0", minHeight: 40, position: "relative", zIndex: 2 }}>
              <IdentitySwitchMenu />
              <MemberCircleList />
            </div>

            {/* 區間 v2：一主（狀態+耗完區間）二輔（商業提醒+上次叫桶） */}
            <div style={{ padding: "28px 22px 0", position: "relative", zIndex: 2, minHeight: 260 }}>
              <div className="ai-hero-text">
                {/* <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  <RiSparkling2Line size={17} color="rgba(255,255,255,0.85)" />
                  <span style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>
                    瓦斯還能用多久
                  </span>
                  {isSurveyBased && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: 6,
                        lineHeight: 1.2,
                        background: "rgba(255,255,255,0.22)",
                        color: "#fff",
                        flexShrink: 0,
                      }}>
                      初步估算
                    </span>
                  )}
                </div> */}

                <GasPredictionHeroContent vm={effectiveVm!} rangeFont={rangeFont} />
              </div>
            </div>
          </div>

          {/* CTA 白卡上緣（與 Hero 重疊） */}
          <div className="ai-cta-sheet-lip">
            {effectiveVm!.showAutoDispatchMessage ? (
              <ReminderStageBlock vm={effectiveVm!} />
            ) : !needSetup ? (
              <div className={showOrderPulse ? "cta-canorder" : undefined} style={{ borderRadius: 16 }}>
                <div className="ai-cta-clearance">
                  <OrderCtaButton vm={effectiveVm!} onClick={goOrder} />
                </div>
              </div>
            ) : null}
          </div>

          <div className="ai-glass-tank">
            <div className="ai-tank-cloud">
              <div className="bubble">
                <svg className="bubble-svg" viewBox="0 0 108 62" aria-hidden>
                  {/* 圓潤對話框；尾巴略長、偏六點鐘方向（略偏右） */}
                  <path
                    d="M24 5
                       C40 1 64 1 80 5
                       C94 10 102 20 102 30
                       C102 40 94 48 82 51
                       C72 53 64 52 60 51
                       L66 60
                       L56 52
                       C44 52 32 49 24 45
                       C12 39 6 30 8 20
                       C10 11 16 7 24 5 Z"
                    fill="#fff"
                    stroke="rgba(255,255,255,0.9)"
                    strokeWidth="1"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                </svg>
                <span
                  className="bubble-label"
                  style={{ color: STATUS_LABEL_COLOR[effectiveVm!.stockStatus] }}>
                  {effectiveVm!.stockStatus}
                </span>
              </div>
            </div>
            <GlassTank percent={tankFill} low={aiLow} tone={effectiveVm!.stockStatus} noData={false} />
          </div>
        </div>
      )}

      {/* ── 白色 sheet（覆蓋 Hero 下緣；AI 預測時 CTA 已併入 ai-hero-cta-zone）── */}
      <div
        style={{
          background: C.page,
          borderRadius: effectiveVm || isColdNew || showSurveyCycleEstimate || showSurveyPredictionPending ? undefined : "24px 24px 0 0",
          /* survey 初估：上接 lip，微上拉消除接縫灰線 */
          marginTop: showSurveyCycleEstimate || showSurveyPredictionPending ? -1 : effectiveVm || isColdNew ? 0 : showColdHero ? -24 : -86,
          position: "relative",
          zIndex: showSurveyCycleEstimate || showSurveyPredictionPending ? 0 : 1,
          padding: showSurveyCycleEstimate || showSurveyPredictionPending
            ? "8px 18px 16px"
            : isColdNew
              ? "6px 18px 16px"
              : effectiveVm
                ? "16px 18px 16px"
                : "24px 18px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}>
        {/* ── Inline 問卷：未填時直接顯示（全新客戶已併入上方主視覺）── */}
        {showSurveyForm && !isColdNew && (
          <InlineSurveyCard
            enableDevQuickFill={IS_DEV && SHOW_DEV_SWITCHER}
            onComplete={handleInlineSurveyComplete}
            reason={surveyNeed.reason}
            initialRecord={surveyNeed.reason === "refresh" ? surveyRecord : null}
          />
        )}

        {/* 冷啟動／0 筆問卷初估 CTA：有問卷時仍顯示（問卷在 Hero 內）；needSetup 時改走橘卡 */}
        {!needSetup && !effectiveVm && (showColdHero || showSurveyCycleEstimate || showSurveyPredictionPending) && (
          <div>
            {/* 脈動框只套在膠囊本身，副標留在框外，否則提示光圈會把兩者一起圈住 */}
            <div className={showOrderPulse ? "cta-canorder" : undefined} style={{ borderRadius: 999 }}>
              <button
                type="button"
                onClick={goOrder}
                className="cta-pill cta-pill--go cta-pill--blue">
                <span className="cta-pill-spacer" aria-hidden="true" />
                <span className="cta-pill-label" style={{ fontSize: 20 }}>立即預約叫桶</span>
                <RiArrowRightLine className="cta-pill-go-icon" size={22} color="#fff" aria-hidden="true" />
              </button>
            </div>
            <div className="cta-pill-sub">線上預約，免通話</div>
          </div>
        )}

        {/* 冷啟動 CTA 後備（無冷啟動 Hero／問卷初估）：小按鈕＋箭頭動畫 */}
        {!needSetup && !effectiveVm && !showSurveyForm && !showSurveyCycleEstimate && !showSurveyPredictionPending && !showColdHero && (
          <div>
            <div className={showOrderPulse ? "cta-canorder" : undefined} style={{ borderRadius: 999 }}>
              <button
                type="button"
                onClick={goOrder}
                className="cta-pill cta-pill--go cta-pill--blue"
                style={{ padding: "16px 22px" }}>
                <span className="cta-pill-spacer" aria-hidden="true" />
                <span className="cta-pill-label" style={{ fontSize: 17 }}>立即預約叫桶</span>
                <RiArrowRightLine className="cta-pill-go-icon" size={22} color="#fff" aria-hidden="true" />
              </button>
            </div>
            <div className="cta-pill-sub">{ctaSubtitle}</div>
          </div>
        )}

        {/* 一般問答（提醒日／區間內／提早）：內容區、瓦斯行上方 */}
        {showInlineAsk && outcomeKind && (
          <CycleOutcomeCard
            key={`inline:${outcomeKind}:${outcomeAnchor}:${devOutcomeForce}:${quickAskNonce}`}
            kind={outcomeKind}
            contextId={chooseIdentity?.context_id}
            anchorDate={outcomeAnchor}
            variant="inline"
            onDismissed={() => setQuickAskDismissed(true)}
            onSubmitAnswer={submitOutcomeAnswer}
            onSubmitPause={submitOutcomePause}
          />
        )}

        {/* 有叫過桶但比對不到（notFound）：顯示「沒有找到您」卡，可加瓦斯行 LINE 人工核對，或以新客戶繼續。
            TODO(後端)：瓦斯行後台快速綁定 + 綁定完成推播通知客戶。 */}
        {showNotFoundCard && (() => {
          const supplierName = vendor?.supplier_name || chooseIdentity?.primary_supplier?.supplier_name || "瓦斯行";
          const cPhone = resData?.customer?.main_phone || "";
          const cAddr = chooseIdentity?.choose_address?.full_address || "";
          const msg =
            `您好，我是${supplierName}的舊客戶，想在 App 綁定既有帳號，請幫我核對：\n` +
            `・姓名：（請補填）\n・電話：${cPhone || "（請補填）"}\n・地址：${cAddr || "（請補填）"}\n（此訊息由叫瓦斯 App 自動帶入）`;
          const lineUrl = `https://line.me/R/oaMessage/${encodeURIComponent("@lpg-demo")}/?${encodeURIComponent(msg)}`;
          return (
            <div style={{ width: "100%", borderRadius: 18, marginTop: 6, background: "#fff", border: "1px solid #E7ECF3", boxShadow: "0 8px 22px -16px rgba(31,41,55,0.5)", overflow: "hidden" }}>
              <div style={{ padding: "16px 16px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <RiErrorWarningLine size={19} color="#D97706" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>沒有找到您的資料</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>您說有叫過瓦斯，但我們比對不到既有資料</div>
                  </div>
                </div>
                <div style={{ fontSize: 12.5, color: "#166534", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 12, padding: "10px 12px", lineHeight: 1.6, marginBottom: 12 }}>
                  加瓦斯行 LINE，訊息已幫您<b>預填核對資料</b>，瓦斯行確認後會為您綁定並通知您。
                </div>
                <a href={lineUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "13px 16px", borderRadius: 13, background: "#06C755", color: "#fff", fontSize: 14.5, fontWeight: 800 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3C6.5 3 2 6.6 2 11c0 3.9 3.5 7.2 8.3 7.9.3.06.75.2.86.46.1.24.06.6.03.85l-.14.83c-.04.24-.2.96.84.52 1.04-.44 5.6-3.3 7.64-5.65C20.9 15.1 22 13.2 22 11c0-4.4-4.5-8-10-8z" />
                    </svg>
                    加 {supplierName} LINE 確認
                  </div>
                </a>
                <button
                  type="button"
                  onClick={() => setNotFound(false)}
                  style={{ width: "100%", marginTop: 8, padding: "11px 16px", borderRadius: 13, border: "1px solid #E7ECF3", background: "#fff", color: C.muted, fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                  先以新客戶開始
                </button>
              </div>
            </div>
          );
        })()}

        {/* 傳單新客戶：首次加入設定入口（放在叫桶按鈕下方、留間距）。
            珊瑚暖色系；左側等化器聲波跳動，語意「分析中／即將開通智慧預測」。 */}
        {needSetup && !showNotFoundCard && (
          <div
            className="fjs-card"
            style={{
              width: "100%", borderRadius: 18, marginTop: 6, overflow: "hidden", position: "relative",
              background: "linear-gradient(135deg,#FB7185 0%,#F97316 100%)",
              boxShadow: "0 12px 26px -14px rgba(244,114,182,0.6)",
            }}>
            <style>{`
              @keyframes fjsCardIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
              @keyframes fjsWave {
                0%, 100% { transform: scaleY(0.35); }
                50% { transform: scaleY(1); }
              }
              .fjs-card { animation: fjsCardIn 0.45s cubic-bezier(0.2,0.9,0.3,1) both; }
              .fjs-wave-bar {
                width: 4.5px;
                height: 22px;
                border-radius: 999px;
                background: #fff;
                transform-origin: center center;
                animation: fjsWave 0.9s ease-in-out infinite;
              }
              .fjs-wave-bar:nth-child(1) { animation-delay: 0s; }
              .fjs-wave-bar:nth-child(2) { animation-delay: 0.15s; }
              .fjs-wave-bar:nth-child(3) { animation-delay: 0.3s; }
              .fjs-wave-bar:nth-child(4) { animation-delay: 0.45s; }
              @media (prefers-reduced-motion: reduce) {
                .fjs-card { animation: none; }
                .fjs-wave-bar { animation: none; transform: scaleY(0.7); }
                .fjs-wave-bar:nth-child(2) { transform: scaleY(1); }
                .fjs-wave-bar:nth-child(3) { transform: scaleY(0.85); }
                .fjs-wave-bar:nth-child(4) { transform: scaleY(0.55); }
              }
            `}</style>

            {/* 等化器聲波：分析中／即將開通智慧預測 */}
            <div style={{ padding: "18px 16px 4px", display: "flex", gap: 14, alignItems: "center" }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.22)",
                  border: "1.5px solid rgba(255,255,255,0.45)",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3.5,
                }}
                aria-hidden>
                <span className="fjs-wave-bar" />
                <span className="fjs-wave-bar" />
                <span className="fjs-wave-bar" />
                <span className="fjs-wave-bar" />
              </div>
              <div style={{ minWidth: 0, flex: 1, paddingRight: 21 }}>
                <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, letterSpacing: -0.2 }}>還差一步</div>
                <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                  完成加入設定，解鎖一鍵叫桶與智慧預測
                </div>
              </div>
            </div>

            {/* 底部 CTA */}
            <div style={{ padding: "14px 16px 16px" }}>
              <button
                type="button"
                onClick={() => { setGateGoOrder(false); setGateOpen(true); }}
                style={{
                  width: "100%", border: "none", cursor: "pointer",
                  background: "#fff",
                  borderRadius: 13, padding: "13px 16px", color: "#BE123C", fontSize: 14.5, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  boxShadow: "0 6px 16px -10px rgba(0,0,0,0.35)",
                }}>
                前往完成設定
                <RiArrowRightLine size={18} color="#BE123C" />
              </button>
            </div>
          </div>
        )}

        {/* 我的瓦斯行 */}
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: C.muted, margin: "0 0 10px 4px" }}>我的瓦斯行</div>
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 15,
              boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
            {vendor?.supplier_name ? (
              <>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: C.blueTint,
                    color: C.blueText,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 500,
                    flexShrink: 0,
                  }}>
                  {getFirstText(vendor.supplier_name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 500, color: C.ink }}>{vendor.supplier_name}</div>
                  {vendor.is_open_now ? (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        marginTop: 4,
                        fontSize: 12,
                        color: C.green,
                        background: C.greenTint,
                        padding: "2px 9px",
                        borderRadius: 12,
                      }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green }} />
                      營業中
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        marginTop: 4,
                        fontSize: 12,
                        color: C.muted,
                        background: "#f1f2f5",
                        padding: "2px 9px",
                        borderRadius: 12,
                      }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.muted }} />
                      尚未營業
                    </span>
                  )}
                </div>
                {vendor.phone && (
                  <a
                    href={`tel:${vendor.phone}`}
                    aria-label="撥打電話"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: C.blueTint,
                      color: C.blue,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                    <RiPhoneLine size={17} color={C.blue} />
                  </a>
                )}
              </>
            ) : (
              <div style={{ fontSize: 14, color: C.muted, padding: "4px 0" }}>尚未有配對的瓦斯行</div>
            )}
          </div>
        </div>

        {/* 優惠券（有可用券才顯示）＋ 我的帳戶摘要：綁定客戶才顯示 */}
        {!needSetup && !showNotFoundCard && (
          <AccountCouponCards token={cusJwtToken} xContext={buildXContextHeader(chooseIdentity)} />
        )}

        {/* AI 智慧回饋（有預測）/ 安全提醒（冷啟動） */}
        {effectiveVm ? (
          <FeedbackBlock
            surveyRecord={showSurveyForm ? null : surveyRecord}
            contextId={chooseIdentity?.context_id}
            behaviorAlert={gasStatus?.behavior_alert ?? null}
            lastOrderDate={lastOrderDate}
            avgDays={avgDays}
            notifyDate={effectiveVm?.notifyStages.find(s => s.customerVisible && s.key === "standard")?.date ?? null}
            suppressBehaviorAlert={showOutcomeCard}
            onUpdateSurvey={() => navigate("/survey")}
          />
        ) : (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "0 0 10px 4px" }}>
              <RiShieldCheckLine size={16} color={C.green} />
              <span style={{ fontSize: 14, fontWeight: 500, color: C.muted }}>安全提醒</span>
            </div>
            <div style={{ background: "#fff", borderRadius: 16, padding: "2px 14px", boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)" }}>
              {[
                { icon: <RiErrorWarningLine size={16} color="#D97706" />, bg: "#FEF3C7", t: "檢查瓦斯軟管", d: "定期確認軟管是否需要更換" },
                { icon: <RiWindyLine size={16} color="#059669" />, bg: "#D1FAE5", t: "使用時保持通風", d: "開窗或啟動抽風，避免一氧化碳" },
                { icon: <RiShutDownLine size={16} color="#3B82F6" />, bg: "#EBF5FF", t: "外出關閉總開關", d: "長時間外出記得關閉瓦斯總開關" },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{ display: "flex", gap: 11, alignItems: "center", padding: "12px 0", borderTop: i > 0 ? "1px solid #F1F2F4" : "none" }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      background: s.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                    {s.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{s.t}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 常見問題快捷（純前端，點擊開啟說明） */}
        {!showNotFoundCard && <FaqCard />}
      </div>

      {/* 急迫問答（過區間未預約／較晚預約）：底部浮層 */}
      {showUrgentSheet && outcomeKind && (
        <CycleOutcomeCard
          key={`sheet:${outcomeKind}:${outcomeAnchor}:${devOutcomeForce}:${quickAskNonce}`}
          kind={outcomeKind}
          contextId={chooseIdentity?.context_id}
          anchorDate={outcomeAnchor}
          variant="sheet"
          onDismissed={() => setQuickAskDismissed(true)}
          onSubmitAnswer={submitOutcomeAnswer}
          onSubmitPause={submitOutcomePause}
        />
      )}

      {/* 「約每 N 天」主動更新用氣習慣 */}
      <SurveyUpdateModal
        open={surveyUpdateOpen}
        busy={surveyTransitioning}
        onClose={() => {
          if (!surveyTransitioning) setSurveyUpdateOpen(false);
        }}
        onComplete={handleSurveyUpdateComplete}
        initialRecord={surveyRecord}
        enableDevQuickFill={IS_DEV && SHOW_DEV_SWITCHER}
      />

      {/* 傳單新客戶：首次加入設定（鋼瓶/配件/合約）；完成後視情況續進訂購。
          skipIdentity：身分已於 onboarding 確認過，這裡不再重複詢問；鐵牌讀碼比對仍保留。 */}
      <FirstJoinSetup
        skipIdentity
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onDone={() => {
          setGateOpen(false);
          if (gateGoOrder) navigate("/gasShop/products");
        }}
      />
    </div>
  );
};

export default HomePage;
